import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaseDetailWithRtw,
  CaseSummary,
  PhasedReturn,
  ReturnToWorkPlan,
  TouchpointSummary,
} from '@besideu/shared/cases';
import type { RosterResponse } from '@besideu/shared/users';
import { api, ApiError } from '@/lib/api.ts';
import { CaseDetailView } from '@/components/CaseDetailView.tsx';
import { CaseNotFound } from '@/components/CaseNotFound.tsx';
import { EmployeePreferencesPanel } from '@/components/EmployeePreferencesPanel.tsx';
import { SignificantDatesPanel } from '@/components/SignificantDatesPanel.tsx';
import { HrMaeveTouchpointGuidance } from '@/components/HrMaeveTouchpointGuidance.tsx';

const PHASED_LABELS: Record<PhasedReturn, string> = {
  Full_Time: 'Full time from day one',
  Part_Time: 'Reduced hours',
  Staggered: 'Staggered ramp',
};

export function HrCaseDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['hr', 'cases', id],
    queryFn: () => api.get<CaseDetailWithRtw>(`/api/hr/cases/${id}`),
    enabled: id !== '',
  });

  // §15/§23 — HR resolves a touch point flagged HR_Review_Needed.
  const completeHrReview = useMutation({
    mutationFn: (touchpointId: string) =>
      api.post<{ touchpoint: TouchpointSummary }>(
        `/api/hr/cases/${id}/touchpoints/${touchpointId}/hr-review-complete`,
        {},
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'cases', id] }),
  });

  // Case-scoped manager reassignment — same dedicated roster source as
  // /hr/members (`GET /api/users/roster?role=Manager`, unpaginated + ALL
  // statuses, NOT the plain paginated `/api/users`). `managers` (selectable
  // reassignment targets) is filtered to Active; the unfiltered roster is
  // kept separately below to resolve `staleMappingManagerName` even when
  // that manager is no longer Active.
  const managerOptions = useQuery({
    queryKey: ['users', 'roster', 'Manager'],
    queryFn: () => api.get<RosterResponse>('/api/users/roster?role=Manager'),
  });
  const managers = (managerOptions.data?.users ?? []).filter((u) => u.status === 'Active');
  const [staleMappingManagerId, setStaleMappingManagerId] = useState<string | null>(null);
  const reassignManager = useMutation({
    mutationFn: (managerId: string) =>
      api.post<{
        case: CaseSummary;
        defaultMappingStillStale: boolean;
        defaultMappingManagerId: string | null;
      }>(`/api/hr/cases/${id}/reassign-manager`, { managerId }),
    onSuccess: (data) => {
      setStaleMappingManagerId(data.defaultMappingStillStale ? data.defaultMappingManagerId : null);
      void queryClient.invalidateQueries({ queryKey: ['hr', 'cases', id] });
      void queryClient.invalidateQueries({ queryKey: ['hr', 'cases'] });
      void queryClient.invalidateQueries({ queryKey: ['hr', 'dashboard'] });
    },
  });
  // Case-only reassignment (deliberate scope trim) never touches the
  // employee's default `user_manager_mappings` entry — resolve the
  // still-stale manager's name from the unfiltered roster (not `managers`,
  // which is Active-only), so this resolves correctly even when the
  // stale-mapped manager has since been deactivated.
  const staleMappingManagerName = useMemo(
    () => managerOptions.data?.users.find((u) => u.id === staleMappingManagerId)?.name ?? null,
    [managerOptions.data, staleMappingManagerId],
  );

  if (detail.isPending) return <p className="text-muted-foreground">Loading support pathway…</p>;
  if (detail.error) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <section className="space-y-6">
          <CaseNotFound
            backTo="/hr/cases"
            reason="It doesn't exist in this organisation, or has been removed."
          />
        </section>
      );
    }
    return <p className="text-traffic-red">Could not load this support pathway.</p>;
  }

  return (
    <section className="space-y-6">
      <Link
        to="/hr/cases"
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to support pathways
      </Link>
      {/* The HR firewall still holds: HR never sees the EMPLOYEE's private Maeve
          conversations. The §15 touch-point guidance below is HR's own
          process/oversight surface (no employee content), so it does not
          breach that boundary. */}
      <CaseDetailView
        data={detail.data}
        onCompleteHrReview={(touchpointId) => completeHrReview.mutate(touchpointId)}
        hrReviewPendingId={completeHrReview.isPending ? (completeHrReview.variables ?? null) : null}
        managers={managerOptions.data ? managers : []}
        onReassignManager={(managerId) => reassignManager.mutate(managerId)}
        reassignPending={reassignManager.isPending}
        reassignError={
          reassignManager.error ? translateReassignManagerError(reassignManager.error) : null
        }
        reassignNote={
          staleMappingManagerName
            ? `Note: ${detail.data.case.employee.name}'s default manager is still ${staleMappingManagerName} — update it on the Members page if that should change too.`
            : null
        }
      />
      <HrMaeveTouchpointGuidance
        caseId={detail.data.case.id}
        touchpoints={detail.data.touchpoints}
      />
      <EmployeePreferencesPanel preferences={detail.data.employeePreferences} />
      <SignificantDatesPanel significantDates={detail.data.significantDates} />
      {detail.data.rtwPlan ? <HrRtwPlanCard plan={detail.data.rtwPlan} /> : null}
    </section>
  );
}

// Slice 18 — HR view of the RTW plan. Read-only by design (HR doesn't
// submit or edit; the Manager owns the plan per DESIGN §6.5). HR sees the
// full row including `managerPrivateNotes` (DESIGN §3.2.2: visibility =
// full row to Manager and HR Safety Contact).
function HrRtwPlanCard({ plan }: { plan: ReturnToWorkPlan }) {
  const acked = plan.acknowledgedAt !== null;
  return (
    <div className="space-y-4 rounded-lg border border-border p-6 text-sm">
      <header className="space-y-1">
        <h2 className="text-base font-medium">Return-to-work plan</h2>
        <p className="text-muted-foreground">
          Submitted by the manager{' '}
          <time dateTime={plan.submittedAt}>
            {new Date(plan.submittedAt).toLocaleString('en-GB')}
          </time>
          .{' '}
          {acked ? (
            <>
              Acknowledged by the employee on{' '}
              <time dateTime={plan.acknowledgedAt!}>
                {new Date(plan.acknowledgedAt!).toLocaleString('en-GB')}
              </time>
              .
            </>
          ) : (
            <span className="text-traffic-amber-text">Awaiting employee acknowledgement.</span>
          )}
        </p>
      </header>
      <dl className="space-y-3">
        <Field label="Workload expectations">{plan.workloadExpectations}</Field>
        <Field label="Phased return">
          {PHASED_LABELS[plan.phasedReturn]}
          {plan.targetFullLoadDate ? ` — target full load by ${plan.targetFullLoadDate}` : ''}
        </Field>
        <Field label="Support preferences">{plan.supportPreferences}</Field>
        {plan.managerPrivateNotes ? (
          <Field label="Private manager notes (not shown to employee)">
            {plan.managerPrivateNotes}
          </Field>
        ) : null}
      </dl>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

// Error translation for the case-scoped manager reassignment. The route
// (routes/hr.ts POST /cases/:id/reassign-manager) returns distinct 404
// codes for the manager vs. the case side of the request.
function translateReassignManagerError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not reassign the manager. Try again.';
  const body = err.body ?? {};
  if (err.status === 404 && body.error === 'manager_not_found') {
    return 'That manager is no longer active — pick another.';
  }
  if (err.status === 404 && body.error === 'not_found') {
    return 'This support pathway could not be found — refresh and try again.';
  }
  if (err.status === 403) {
    return 'You don’t have permission to reassign this support pathway.';
  }
  return 'Could not reassign the manager. Try again.';
}
